import React, { useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import {
  ContentAsset,
  PlaylistPlaylistTypeEnum as PlaylistTypeEnum,
} from '../../swagger'
import { Box, Card, IconButton, Slider, Stack, Typography } from '@mui/material'
import { PauseRounded, PlayArrowRounded, VolumeUp } from '@mui/icons-material'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import CC_Seal from '../../Images/Branding/cc-seal.png'
import { useNavigate } from 'react-router'
import EmptyPage from '../Elements/EmptyPage'
import { useTranslation } from 'react-i18next'

interface CCPlayerProps {
  playlist: (ContentAsset & { playlistKey: number; playlistItemKey: number })[]
  loop?: boolean // If true, loops back to the first item after the last
  PlaylistItemKey: number // Key of the item to start playing
  playlistType: PlaylistTypeEnum // Used to build navigation paths
}

const baseURL = process.env.REACT_APP_BASE_URL_ASSETS ?? ''
const queryParam = '?u=zq6gep'
const getURL = (inputUrl: string) => {
  const patterns = ['/view/video/', '/content/']
  const pattern = patterns.find((p) => inputUrl.includes(p))
  if (!pattern) return 'Unsupported URL format'
  const fileId = inputUrl.split(pattern)[1]?.split('/')[0]
  const filename = inputUrl.split('/').pop()?.split('?')[0]
  if (!fileId || !filename) return 'Unsupported URL format'
  return `${baseURL}${fileId}/original/${filename}${queryParam}`
}

const CCPlayer: React.FC<CCPlayerProps> = ({
  playlist,
  loop = false,
  PlaylistItemKey,
  playlistType,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Current item index in the playlist
  const [currentIndex, setCurrentIndex] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [volume, setVolume] = useState(0.9)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const playerRef = useRef<ReactPlayer>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [playbackState, setPlaybackState] = useState({
    played: 0,
    playedSeconds: 0,
    duration: 0,
  })

  const isFs = !!document.fullscreenElement

  // Set the currentIndex when PlaylistItemKey is provided
  useEffect(() => {
    if (PlaylistItemKey && playlist.length > 0) {
      const index = playlist.findIndex(
        (item) => item.playlistItemKey === PlaylistItemKey
      )
      if (index !== -1) {
        setCurrentIndex(index)
      }
    }
  }, [PlaylistItemKey, playlist])

  useEffect(() => {
    if (!isFs && isFullscreen) setIsFullscreen(false)
  }, [isFs, isFullscreen])

  const handlePlayPause = () => setPlaying((prev) => !prev)

  const handleSeek = (_event: Event, value: number | number[]) => {
    const fraction = (Array.isArray(value) ? value[0] : value) / 100
    playerRef.current?.seekTo(fraction, 'fraction')
  }

  const handleVolumeChange = (_event: Event, value: number | number[]) => {
    const vol = Array.isArray(value) ? value[0] : value
    setVolume(vol / 100)
  }

  const handleProgress = (state: { played: number; playedSeconds: number }) => {
    setPlaybackState({
      ...playbackState,
      played: state.played,
      playedSeconds: state.playedSeconds,
    })
  }

  const handleDuration = (dur: number) => {
    setPlaybackState({
      ...playbackState,
      duration: dur,
    })
  }
  // Handle end of playback: move to next item or loop back to first
  const handleEnded = () => {
    // Build the base path for navigation by removing "Center" from playlistType and converting to lowercase
    const resourcePath = playlistType.replace('Center', '').toLowerCase()
    // Check if there is a next item in the playlist
    if (currentIndex < playlist.length - 1) {
      setCurrentIndex((prev) => {
        const nextItem = prev + 1 // Compute the index of the next item
        // Navigate to the next item's URL
        navigate(
          `/${resourcePath}/playlist/content/${playlist[nextItem].playlistKey}/${playlist[nextItem].playlistItemKey}`
        )
        return nextItem // Update the currentIndex state
      })
      // If already at the end of the playlist, but looping is enabled
    } else if (loop) {
      setCurrentIndex(0) // Reset to the first item
      // Navigate to the first item's URL
      navigate(
        `/${resourcePath}/playlist/content/${playlist[0].playlistKey}/${playlist[0].playlistItemKey}`
      )
    }
  }

  const toggleFullscreen = () => {
    const containerPlayer = containerRef.current

    if (!containerPlayer) return

    if (!document.fullscreenElement) {
      setIsFullscreen(true)
      containerPlayer.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable fullscreen', err)
      })
    } else {
      setIsFullscreen(false)
      document.exitFullscreen()
    }
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  if (playlist.length === 0) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'CCPlayer.Message.NoPlaylistData',
              'There are no items available to play right now.'
            )}
          </Typography>
        }
      />
    )
  }

  const currentUrl = playlist[currentIndex]

  const isAudio = currentUrl.filetype === 'Audio'

  return (
    <Card
      ref={containerRef}
      onClick={handlePlayPause}
      sx={{
        margin: '0 auto',
        color: 'white',
        background: isAudio ? 'white' : 'black',
        maxWidth: isFullscreen ? '100%' : isAudio ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : isAudio ? '100%' : '100%',
        position: 'relative',
        '::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: isAudio ? `url(${CC_Seal})` : undefined,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 0,
        },
      }}
    >
      <ReactPlayer
        key={currentUrl.playlistItemKey}
        ref={playerRef}
        style={{ width: '100%' }}
        url={getURL(currentUrl.embedUrl ?? '')}
        playing={playing}
        volume={volume}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onEnded={handleEnded}
        controls={false}
        width="100%"
        height="100%"
        basename={'true'}
        playsinline
        config={{
          file: {
            attributes: {
              preload: 'auto', // o 'metadata', 'none'
            },
          },
        }}
        onError={(error) =>
          (error ?? ''.includes(`didn't interact`)) && setPlaying(false)
        }
      />

      {/* Custom Controls */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          zIndex: 10,
          color: 'inherit',
          px: 2,
          py: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          color={'inherit'}
          aria-label="play/pause"
          onClick={() => setPlaying((prev) => !prev)}
        >
          {playing ? (
            <PauseRounded fontSize="medium" />
          ) : (
            <PlayArrowRounded fontSize="medium" />
          )}
        </IconButton>

        <Typography
          color={'inherit'}
          sx={{
            minWidth: 40,
          }}
        >
          {formatTime(playbackState.playedSeconds)}
        </Typography>

        <Slider
          aria-label="progress"
          value={playbackState.played * 100}
          onChange={handleSeek}
          sx={{
            height: 4,
            color: 'inherit',
            '& .MuiSlider-thumb': {
              width: 8,
              height: 8,
              transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
              '&::before': {
                boxShadow: '0 2px 12px rgba(233, 223, 223, 0.4)',
              },
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(0, 0, 0, 0.16)',
              },
              '&.Mui-active': {
                width: 20,
                height: 20,
              },
            },
            '& .MuiSlider-rail': {
              opacity: 0.28,
            },
          }}
        />

        <Typography
          color={'inherit'}
          component={'span'}
          sx={{
            minWidth: 40,
          }}
        >
          {formatTime(playbackState.duration)}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover .volume-slider': {
              opacity: 1,
              pointerEvents: 'auto',
            },
          }}
        >
          <IconButton color="inherit">
            <VolumeUp />
          </IconButton>
          <Slider
            className="volume-slider"
            aria-label="Volume"
            value={volume * 100}
            onChange={handleVolumeChange}
            orientation={'vertical'}
            sx={{
              position: 'absolute',
              bottom: '120%',
              left: '50%',
              transform: 'translateX(-50%)',
              height: 100,
              width: 4,
              color: 'inherit',
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.2s ease',
              bgcolor: 'rgba(0,0,0,0.3)',
              borderRadius: 1,
            }}
          />
        </Box>

        <IconButton
          color={'inherit'}
          onClick={toggleFullscreen}
          aria-label="Toggle Fullscreen"
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Stack>
    </Card>
  )
}

export default CCPlayer
